import { logger } from './logger';

type AnyClient = any;

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn('Missing Supabase environment variables. Authentication features will be disabled.');
}

let client: AnyClient | null = null;
let pending: Promise<AnyClient> | null = null;

function createMockClient(): AnyClient {
  return {
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      signInWithPassword: () =>
        Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      signUp: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      signOut: () => Promise.resolve({ error: { message: 'Supabase not configured' } }),
      resetPasswordForEmail: () =>
        Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
    },
    from: () => ({
      select: () => ({
        maybeSingle: () =>
          Promise.resolve({
            data: null,
            error: { message: 'Supabase not configured properly. Please check your environment variables.' },
          }),
        eq: () => ({ maybeSingle: () => Promise.resolve({ data: null, error: null }) }),
        single: () => Promise.resolve({ data: null, error: null }),
      }),
      update: () => ({
        eq: () => Promise.resolve({ data: null, error: null }),
      }),
    }),
  };
}

/**
 * Lazy Supabase client — `@supabase/supabase-js` is NOT in the homepage critical path.
 * First call downloads supabase-vendor (~idle / auth interaction).
 */
export function getSupabase(): Promise<AnyClient> {
  if (client) return Promise.resolve(client);
  if (pending) return pending;

  pending = (async () => {
    if (!supabaseUrl || !supabaseAnonKey) {
      logger.info('Creating mock Supabase client due to missing credentials');
      client = createMockClient();
      return client;
    }
    try {
      const { createClient } = await import('@supabase/supabase-js');
      client = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
        },
      });
      return client;
    } catch (error) {
      logger.error('Failed to create Supabase client. Using mock client instead:', error);
      client = createMockClient();
      return client;
    }
  })();

  return pending;
}

const CHAIN_METHODS = new Set([
  'select',
  'insert',
  'update',
  'upsert',
  'delete',
  'eq',
  'neq',
  'gt',
  'lt',
  'gte',
  'lte',
  'like',
  'ilike',
  'is',
  'in',
  'contains',
  'containedBy',
  'order',
  'limit',
  'range',
  'match',
  'filter',
  'not',
  'or',
  'textSearch',
]);

const TERMINAL = new Set(['single', 'maybeSingle', 'csv', 'throwOnError']);

function createDeferredFrom(table: string): AnyClient {
  const ops: Array<(b: AnyClient) => AnyClient> = [];

  const run = async () => {
    const sb = await getSupabase();
    let b = sb.from(table);
    for (const op of ops) b = op(b);
    return b;
  };

  const api: AnyClient = {
    then(onFulfilled: (v: unknown) => unknown, onRejected?: (e: unknown) => unknown) {
      return run().then(onFulfilled, onRejected);
    },
  };

  for (const m of CHAIN_METHODS) {
    api[m] = (...args: unknown[]) => {
      ops.push((b) => b[m](...args));
      return api;
    };
  }

  for (const m of TERMINAL) {
    api[m] = (...args: unknown[]) =>
      getSupabase().then(async (sb) => {
        let b = sb.from(table);
        for (const op of ops) b = op(b);
        return b[m](...args);
      });
  }

  return api;
}

/**
 * Backward-compatible facade: auth methods + from() defer to getSupabase().
 * Does not statically import @supabase/supabase-js (CWV).
 */
export const supabase: AnyClient = {
  get auth() {
    return {
      getSession: () => getSupabase().then((s) => s.auth.getSession()),
      onAuthStateChange: (cb: (...args: unknown[]) => void) => {
        let unsub = () => {};
        let ready = false;
        getSupabase().then((s) => {
          const { data } = s.auth.onAuthStateChange(cb);
          unsub = () => data.subscription.unsubscribe();
          ready = true;
        });
        return {
          data: {
            subscription: {
              unsubscribe: () => {
                if (ready) unsub();
                else getSupabase().then(() => unsub());
              },
            },
          },
        };
      },
      signInWithPassword: (...args: unknown[]) =>
        getSupabase().then((s) => s.auth.signInWithPassword(...args)),
      signUp: (...args: unknown[]) => getSupabase().then((s) => s.auth.signUp(...args)),
      signOut: () => getSupabase().then((s) => s.auth.signOut()),
      resetPasswordForEmail: (...args: unknown[]) =>
        getSupabase().then((s) => s.auth.resetPasswordForEmail(...args)),
      exchangeCodeForSession: (...args: unknown[]) =>
        getSupabase().then((s) => s.auth.exchangeCodeForSession(...args)),
      verifyOtp: (...args: unknown[]) =>
        getSupabase().then((s) => s.auth.verifyOtp(...args)),
      setSession: (...args: unknown[]) =>
        getSupabase().then((s) => s.auth.setSession(...args)),
      updateUser: (...args: unknown[]) =>
        getSupabase().then((s) => s.auth.updateUser(...args)),
      getUser: () => getSupabase().then((s) => s.auth.getUser()),
    };
  },
  from: (table: string) => createDeferredFrom(table),
};
